import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { trackPageView } from "../lib/analytics";

/**
 * Fires a GA4 `page_view` event on every React Router route change.
 *
 * Why this exists: GA4's gtag.js auto-page-view fires only on the initial
 * document load. Single-page apps mount once and then change URL via
 * `history.pushState` — gtag has no way to know about those changes.
 * Without this component, GA4 would only ever see the user's first URL
 * and the entire session would look like a 1-page bounce.
 *
 * We:
 *   1. Wait for `document.title` to actually reflect the new route (Helmet
 *      updates it on the next microtask after route change).
 *   2. Skip firing if pathname hasn't changed (handles `replaceState`
 *      called by anchor scrolling, which would otherwise double-fire).
 *   3. Render nothing — this is a pure side-effect component mounted
 *      once at the top of <App>.
 */
export default function RouteAnalytics() {
  const location = useLocation();
  const lastPath = useRef(null);

  useEffect(() => {
    const fullPath = location.pathname + location.search;
    if (lastPath.current === fullPath) return;
    lastPath.current = fullPath;

    // Defer one tick so react-helmet-async has a chance to apply <title>
    // updates before we read document.title.
    const t = setTimeout(() => {
      trackPageView(fullPath, document.title);
    }, 0);
    return () => clearTimeout(t);
  }, [location.pathname, location.search]);

  return null;
}
